// ============================================================================
// «Mis empresas» — several zBilling accounts one click apart in this browser.
// ----------------------------------------------------------------------------
// Every account (company) is its own Supabase user: its data, plan, team and
// Verifactu chain stay separate, and nothing in the row-level security changes.
// What this module adds is a list of the sessions this browser has signed in
// to, so the user switches company without typing a password each time:
//
//   localStorage zb.accounts = [{ user_id, email, company, access_token,
//                                  refresh_token, saved_at }]
//
// Supabase rotates refresh tokens, so the entry of the ACTIVE account is
// refreshed on every auth change (rememberCurrent). Switching calls
// supabase.auth.setSession() with the stored pair; if it has expired the
// caller asks for the password again (signInAndRemember).
// ============================================================================
import { supabase } from './supabase';

export const STORAGE_KEY = 'zb.accounts';
const MAX_ACCOUNTS = 8;

function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch { return []; }
}

function write(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ACCOUNTS))); } catch { /* storage blocked */ }
}

/** Pure: upsert one entry (by user_id) at the front of the list. */
export function upsertAccount(list, entry) {
  const rest = (list || []).filter((a) => a.user_id !== entry.user_id);
  return [{ ...entry, saved_at: Date.now() }, ...rest].slice(0, MAX_ACCOUNTS);
}

/** Pure: drop one entry. */
export function removeAccount(list, userId) {
  return (list || []).filter((a) => a.user_id !== userId);
}

export function listAccounts() { return read(); }

/** Keep the active session's tokens fresh in the list (call on auth changes). */
export function rememberCurrent(session, company) {
  if (!session?.user?.id || !session.refresh_token) return read();
  const list = upsertAccount(read(), {
    user_id: session.user.id,
    email: session.user.email || '',
    company: company || read().find((a) => a.user_id === session.user.id)?.company || '',
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });
  write(list);
  return list;
}

export function forget(userId) {
  const list = removeAccount(read(), userId);
  write(list);
  return list;
}

/**
 * Switch to a remembered account. Resolves { ok } or { ok: false, expired: true }
 * when the stored refresh token no longer works (Supabase rotated it elsewhere).
 */
export async function switchTo(userId) {
  const entry = read().find((a) => a.user_id === userId);
  if (!entry) return { ok: false, error: 'unknown' };
  const current = (await supabase.auth.getSession()).data.session;
  if (current) rememberCurrent(current);
  const { data, error } = await supabase.auth.setSession({ access_token: entry.access_token, refresh_token: entry.refresh_token });
  if (error || !data?.session) return { ok: false, expired: true, error: error?.message };
  rememberCurrent(data.session, entry.company);
  return { ok: true };
}

/** Sign in to another account with its password and remember both sessions. */
export async function signInAndRemember(email, password) {
  const current = (await supabase.auth.getSession()).data.session;
  if (current) rememberCurrent(current);
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error || !data?.session) return { ok: false, error: error?.message || 'sign-in failed' };
  rememberCurrent(data.session);
  return { ok: true, userId: data.session.user.id };
}
